import {
  CaretRightIcon,
  CheckIcon,
  CircledIcon,
  ErrorMessage,
  ExclamationMarkIcon,
  FlagIcon,
  getOwnerIdFromSettings,
  ImportantMessage,
  ListChecksIcon,
  ModalDialog,
  Mtds,
  ScreenHeader,
  TestTubeIcon,
  TopBar,
  AppLayout,
  useAuth,
  useAvdeling,
  useGetImportantMessagesQuery,
  useGetNewsArticleProveTaQuery,
  useGetOwnerNameQuery,
  useGetSamplesInfiniteQuery,
  useGetTeamQuery,
  useGetTasksQuery,
  WhatsNew,
  PackageIcon
} from 'prove-common'
import { Link } from 'react-router'
import { getTaskList, groupTaskList } from '../utils/taskHelper'
import { AppLogo } from '../components/app-logo/AppLogo'
import { ReactNode, useState } from 'react'
import { SideBar } from '../components/side-bar/SideBar'

const getGreeting = () => {
  const currentHour = new Date().getHours()
  if (currentHour < 10) {
    return 'God morgen'
  } else if (currentHour < 12) {
    return 'God formiddag'
  } else if (currentHour < 18) {
    return 'God ettermiddag'
  } else {
    return 'God kveld'
  }
}

const DateDisplay = () => (
  <Mtds.Muted
    as='strong'
    className='mt-2 px-3 py-1 w-fit rounded-full bg-gradient-to-r from-danger-base-default to-warning-base-default text-white'
  >
    {new Date()
      .toLocaleDateString('no-NO', {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(/^\w/, (c) => c.toUpperCase())}
  </Mtds.Muted>
)

const GreetingMessage = () => {
  const auth = useAuth()
  return (
    <ScreenHeader
      title={
        <>
          {getGreeting()}, {auth.accountName} 🍵
        </>
      }
      description={<p>Her er alt du trenger for å holde deg oppdatert</p>}
    />
  )
}

type NavigationCardProps = {
  icon: ReactNode
  label: string
  to: string
}

const NavigationCard = ({ icon, label, to }: NavigationCardProps) => (
  <Mtds.Card
    as={Link}
    className='bg-surface-tinted hover:bg-surface-hover'
    data-pad='6'
    to={to}
  >
    <Mtds.Grid data-justify='center'>
      <CircledIcon icon={icon} className='bg-surface-default!' />
      <strong>{label}</strong>
    </Mtds.Grid>
  </Mtds.Card>
)

const TeamSelector = () => {
  const settings = useAvdeling()
  const selectedTeamId = settings.get?.selectedTeamId
  const selectedSeksjonId = settings.get?.selectedSeksjonId
  const ownerId = selectedTeamId == null ? undefined : `team:${selectedTeamId}`
  const {
    data: ownerName,
    isPending: isPendingOwnerName,
    isError: isErrorOwnerName,
    error: ownerNameError
  } = useGetOwnerNameQuery(ownerId)
  const {
    data: teams,
    isPending: isPendingTeams,
    isError: isErrorTeams,
    error: teamsError
  } = useGetTeamQuery(
    selectedTeamId == null ? undefined : selectedSeksjonId,
    selectedTeamId == null ? undefined : settings.get?.selectedAvdelingId
  )

  if (selectedTeamId == null) return null
  if (isErrorOwnerName || isErrorTeams) {
    return <ErrorMessage error={ownerNameError ?? teamsError} />
  }

  return (
    <Mtds.Flex data-align='center'>
      <Mtds.Heading as='h2' data-size='xs'>
        <Mtds.Button
          data-arrow
          className='p-0 [font-weight:inherit] [--mtdsc-button-size:var(--mtds-6)] hover:bg-background-tinted'
          popoverTarget='home-team-selector'
          disabled={isPendingTeams}
        >
          {isPendingOwnerName ? (
            <Mtds.Skeleton />
          ) : (
            ownerName?.name?.replace(/./, (m) => m.toUpperCase())
          )}
        </Mtds.Button>
      </Mtds.Heading>
      <Mtds.Popover
        as='menu'
        id='home-team-selector'
        data-color='neutral'
        data-placement='bottom-start'
      >
        {(teams ?? []).map((team) => (
          <li key={team.id}>
            <Mtds.Button
              className={
                team.id === selectedTeamId
                  ? 'bg-surface-tinted hover:bg-surface-tinted'
                  : 'hover:bg-background-tinted'
              }
              popoverTarget='home-team-selector'
              popoverTargetAction='hide'
              onClick={() =>
                settings.set({
                  ...settings.get,
                  selectedTeamId: team.id
                })
              }
            >
              {team.id === selectedTeamId && <CheckIcon />}
              {team.name}
            </Mtds.Button>
          </li>
        ))}
      </Mtds.Popover>
    </Mtds.Flex>
  )
}

type NewSampleResultsCardProps = {
  numberOfSamplesReadyToProcess?: number
  isPending?: boolean
  navigateTo: string
}

const NewSampleResultsCard = ({
  numberOfSamplesReadyToProcess,
  isPending,
  navigateTo
}: NewSampleResultsCardProps) => (
  <Mtds.Card as={Link} to={navigateTo}>
    <Mtds.Flex
      data-color='danger'
      data-justify='space-between'
      data-align='center'
      data-nowrap
    >
      <Mtds.Flex data-nowrap data-align='center'>
        <CircledIcon icon={<FlagIcon />} className='text-text-default' />
        <Mtds.Info>
          <Mtds.Muted>Nye prøvesvar</Mtds.Muted>
          {isPending ? (
            <Mtds.Skeleton />
          ) : (
            <p>
              <span className={'font-bold'}>
                {numberOfSamplesReadyToProcess}
              </span>{' '}
              {numberOfSamplesReadyToProcess === 1
                ? 'prøvesvar klart til behandling'
                : 'prøvesvar klare til behandling'}
            </p>
          )}
        </Mtds.Info>
      </Mtds.Flex>
      <CaretRightIcon />
    </Mtds.Flex>
  </Mtds.Card>
)

type MarkSampleSendtCardProps = {
  numberOfSamplesReadyToSend?: number
  isPending?: boolean
  navigateTo: string
}

const MarkSampleSendtCard = ({
  numberOfSamplesReadyToSend,
  isPending,
  navigateTo
}: MarkSampleSendtCardProps) => (
  <Mtds.Card as={Link} to={navigateTo}>
    <Mtds.Flex
      data-color='neutral'
      data-justify='space-between'
      data-align='center'
      data-nowrap
    >
      <Mtds.Flex data-nowrap data-align='center'>
        <CircledIcon icon={<PackageIcon />} className='text-text-default' />
        <Mtds.Info>
          <Mtds.Muted>Merk prøver som sendt</Mtds.Muted>
          {isPending ? (
            <Mtds.Skeleton />
          ) : (
            <p>
              <span className={'font-bold'}>{numberOfSamplesReadyToSend}</span>{' '}
              prøver klare til å merkes
            </p>
          )}
        </Mtds.Info>
      </Mtds.Flex>
      <CaretRightIcon />
    </Mtds.Flex>
  </Mtds.Card>
)

const TaskInfoCard = () => {
  const settings = useAvdeling()
  const selectedTeamId = settings.get?.selectedTeamId
  const ownerIdFilter = getOwnerIdFromSettings(settings.get)
  const {
    data: tasks,
    isPending: isPendingTasks,
    isError: isErrorTasks,
    error: tasksError
  } = useGetTasksQuery({
    ownerId: ownerIdFilter == null ? undefined : [ownerIdFilter]
  })
  const taskList = getTaskList(tasks ?? [], undefined)
  const groupedTaskList = groupTaskList(taskList, undefined)
  const {
    data: ownerName,
    isPending: isPendingOwnerName,
    isError: isErrorOwnerName,
    error: ownerNameError
  } = useGetOwnerNameQuery(ownerIdFilter)
  const { data: samples, isPending: isPendingSamples } =
    useGetSamplesInfiniteQuery({
      isEnabled: ownerIdFilter != null,
      ownerScope: 'subtree',
      ownerIds: ownerIdFilter == null ? undefined : [ownerIdFilter],
      status: ['ReadyToProcess'],
      sort: 'takenDate,desc'
    })

  const { data: samplesToSend, isPending: isPendingSamplesToSend } =
    useGetSamplesInfiniteQuery({
      isEnabled: ownerIdFilter != null,
      ownerScope: 'subtree',
      ownerIds: ownerIdFilter == null ? undefined : [ownerIdFilter],
      status: ['SampleCreated'],
      sort: 'takenDate,desc'
    })

  const numberOfSamplesReadyToSend = samplesToSend?.pages[0].totalElements

  const isPending =
    isPendingTasks ||
    isPendingOwnerName ||
    settings.isPending ||
    isPendingSamples ||
    isPendingSamplesToSend
  const isError = isErrorTasks || isErrorOwnerName || settings.isError
  const error = tasksError ?? ownerNameError

  const tasksCount = groupedTaskList.length
  const completedTasksCount = groupedTaskList.filter(
    (task) => task.isCompleted
  ).length
  const samplesTaken = taskList.reduce(
    (acc, { task }) => acc + (task.totalCount ?? 0),
    0
  )
  const samplesToTake = taskList.reduce(
    (acc, { task }) => acc + (task.quantity ?? 0),
    0
  )

  const numberOfSamplesReadyToProcess = samples?.pages[0].totalElements

  if (isError) return <ErrorMessage error={error} />

  return (
    <Mtds.Group as={Mtds.Grid}>
      {selectedTeamId == null ? (
        <Mtds.Heading as='h2' data-size='xs'>
          {isPendingOwnerName ? (
            <Mtds.Skeleton />
          ) : (
            ownerName?.name?.replace(/./, (m) => m.toUpperCase())
          )}
        </Mtds.Heading>
      ) : (
        <TeamSelector />
      )}
      <Mtds.Grid data-items='100' className='h-full'>
        <Mtds.Card as={Link} to='/tasks?tab=1' className='h-full'>
          <Mtds.Grid data-align-content={'space-between'} className='h-full'>
            <Mtds.Flex data-justify='space-between' data-nowrap>
              <Mtds.Muted className='leading-none'>
                Oppgaver fullført
              </Mtds.Muted>
              <ListChecksIcon className='h-5 w-5 align-middle' />
            </Mtds.Flex>
            {isPending ? (
              <Mtds.Skeleton className={'h-13'} />
            ) : (
              <div className='flex flex-col gap-2'>
                <span className='inline-flex flex-wrap items-baseline gap-1'>
                  <strong data-size='lg'>{completedTasksCount}</strong> av{' '}
                  {tasksCount}
                </span>
                <Mtds.Progress value={completedTasksCount} max={tasksCount} />
              </div>
            )}
          </Mtds.Grid>
        </Mtds.Card>
        <Mtds.Card as={Link} to='/samples?tab=0' className='h-full'>
          <Mtds.Grid data-align-content={'space-between'} className='h-full'>
            <Mtds.Flex data-justify='space-between' data-nowrap>
              <Mtds.Muted className='leading-none'>Prøver tatt</Mtds.Muted>
              <TestTubeIcon className='h-5 w-5 align-middle' />
            </Mtds.Flex>
            {isPending ? (
              <Mtds.Skeleton className={'h-13'} />
            ) : (
              <div className='flex flex-col gap-2'>
                <span className='inline-flex flex-wrap items-baseline gap-1'>
                  <strong data-size='lg'>{samplesTaken}</strong> av{' '}
                  {samplesToTake}
                </span>
                <Mtds.Progress value={samplesTaken} max={samplesToTake} />
              </div>
            )}
          </Mtds.Grid>
        </Mtds.Card>
      </Mtds.Grid>
      <NewSampleResultsCard
        numberOfSamplesReadyToProcess={numberOfSamplesReadyToProcess}
        isPending={isPendingSamples}
        navigateTo={`/samples-to-process?ownerId=${ownerIdFilter}`}
      />
      <MarkSampleSendtCard
        numberOfSamplesReadyToSend={numberOfSamplesReadyToSend}
        isPending={isPendingSamplesToSend}
        navigateTo={`/samples-to-mark-as-sent?ownerId=${ownerIdFilter}`}
      />
    </Mtds.Group>
  )
}

const ImportantMessageCardContent = ({
  messageTitle
}: {
  messageTitle?: string
}) => (
  <Mtds.Flex data-color='warning' data-align='center' data-nowrap>
    <CircledIcon icon={<ExclamationMarkIcon />} />
    <Mtds.Info>
      <Mtds.Muted>Viktig melding</Mtds.Muted>
      <strong>{messageTitle}</strong>
    </Mtds.Info>
  </Mtds.Flex>
)

const ImportantMessageCard = ({
  messageTitle,
  onClick
}: {
  messageTitle?: string
  onClick: () => void
}) => (
  <Mtds.Card
    onClick={onClick}
    as={Mtds.Flex}
    data-justify='space-between'
    data-align='center'
    data-nowrap
    style={{ cursor: 'pointer' }}
    className='transition-shadow hover:shadow-lg focus:shadow-lg border border-transparent hover:border-black focus:border-black cursor-pointer'
  >
    <ImportantMessageCardContent messageTitle={messageTitle} />
    <CaretRightIcon />
  </Mtds.Card>
)

const ForYouCard = () => {
  const [openMessage, setOpenMessage] = useState<ImportantMessage | null>(null)
  const { data, isPending: isPendingSamples } = useGetSamplesInfiniteQuery({
    isEnabled: true,
    userId: 'self',
    status: ['ReadyToProcess'],
    sort: 'takenDate,desc'
  })
  const { data: samplesToSend, isPending: isPendingSamplesToSend } =
    useGetSamplesInfiniteQuery({
      isEnabled: true,
      userId: 'self',
      status: ['SampleCreated'],
      sort: 'takenDate,desc'
    })
  const { data: importantMessages } = useGetImportantMessagesQuery(true)
  const numberOfSamplesReadyToProcess = data?.pages[0].totalElements
  const numberOfSamplesReadyToSend = samplesToSend?.pages[0].totalElements

  return (
    <Mtds.Group as={Mtds.Grid}>
      <Mtds.Heading data-size='xs'>For deg</Mtds.Heading>
      <NewSampleResultsCard
        numberOfSamplesReadyToProcess={numberOfSamplesReadyToProcess}
        isPending={isPendingSamples}
        navigateTo={`/samples-to-process?userId=self`}
      />
      <MarkSampleSendtCard
        numberOfSamplesReadyToSend={numberOfSamplesReadyToSend}
        isPending={isPendingSamplesToSend}
        navigateTo={`/samples-to-mark-as-sent?userId=self`}
      />
      {importantMessages?.map((message) => (
        <ImportantMessageCard
          key={message.id}
          messageTitle={message.title}
          onClick={() => setOpenMessage(message)}
        />
      ))}
      <ModalDialog
        id='important-message-modal'
        closedby='any'
        open={!!openMessage}
        onClose={() => setOpenMessage(null)}
      >
        <Mtds.Button
          command='close'
          commandfor={'important-message-modal'}
          aria-label={'Lukk'}
        />
        <Mtds.Prose>
          <ImportantMessageCardContent messageTitle={openMessage?.title} />
          <p className='break-words'>{openMessage?.text}</p>
        </Mtds.Prose>
      </ModalDialog>
    </Mtds.Group>
  )
}

export const HomeScreen = () => {
  const { data: articleData } = useGetNewsArticleProveTaQuery()
  return (
    <AppLayout
      sidebar={<SideBar />}
      topBar={<TopBar logo={<AppLogo />} title={'Hjem'} />}
    >
      <Mtds.Grid>
        <DateDisplay />
        <GreetingMessage />
        <Mtds.Grid data-items='200' className='grid-cols-2'>
          <NavigationCard
            icon={<ListChecksIcon />}
            label='Oppgaver'
            to='/tasks'
          />
          <NavigationCard
            icon={<TestTubeIcon />}
            label='Prøver'
            to='/samples'
          />
        </Mtds.Grid>
        <TaskInfoCard />
        <ForYouCard />
      </Mtds.Grid>
      {articleData && <WhatsNew article={articleData} />}
    </AppLayout>
  )
}
